(() => {
  const panels = Array.from(document.querySelectorAll('[data-hg15-panel]'));
  if (!panels.length) return;

  // Editorial films autoplay silently when visible and motion is permitted.
  const ratios = new Map();
  let active = null;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const automaticMotionAllowed = () => !reducedMotion.matches && document.documentElement.dataset.motion !== 'paused';

  const updateAccessibility = () => {
    for (const panel of panels) {
      const video = panel.querySelector('[data-hg15-video]');
      const still = panel.querySelector('.hg15-film__still');
      const ready = panel.querySelector('.hg15-film__frame').classList.contains('is-ready');
      if (ready) {
        video.removeAttribute('aria-hidden');
        still.setAttribute('aria-hidden', 'true');
      } else {
        video.setAttribute('aria-hidden', 'true');
        still.removeAttribute('aria-hidden');
      }
    }
  };

  const pause = (panel) => {
    const video = panel.querySelector('[data-hg15-video]');
    panel.dataset.playRequested = 'false';
    panel.dataset.manualPlay = 'false';
    video.autoplay = false;
    video.pause();
    const control = panel.querySelector('[data-hg15-control]');
    control.textContent = 'Play video';
    control.setAttribute('aria-label', 'Play video');
  };

  const load = (panel) => {
    const video = panel.querySelector('[data-hg15-video]');
    if (video.dataset.failed || video.src || !video.dataset.src) return;
    video.src = window.matchMedia('(max-width: 600px)').matches && video.dataset.mobileSrc
      ? video.dataset.mobileSrc
      : video.dataset.src;
    video.load();
  };

  const play = (panel, manual = false) => {
    const video = panel.querySelector('[data-hg15-video]');
    if (video.dataset.failed || !video.dataset.src) return;
    if (panel.dataset.playRequested === 'true') return;
    panel.dataset.playRequested = 'true';
    panel.dataset.manualPlay = String(manual);
    video.muted = true;
    video.defaultMuted = true;
    video.autoplay = !manual && automaticMotionAllowed();
    load(panel);
    for (const other of panels) if (other !== panel) pause(other);
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {
      if (video.paused) pause(panel);
    });
  };

  const choose = () => {
    if (document.hidden) {
      if (active) pause(active);
      active = null;
      updateAccessibility();
      return;
    }
    let next = null;
    let highest = .15;
    for (const panel of panels) {
      const ratio = ratios.get(panel) || 0;
      if (ratio > highest) { next = panel; highest = ratio; }
    }
    if (active && active !== next) pause(active);
    active = next;
    if (next && automaticMotionAllowed() && next.dataset.userPaused !== 'true') play(next);
    updateAccessibility();
  };

  for (const panel of panels) {
    const frame = panel.querySelector('.hg15-film__frame');
    const video = panel.querySelector('[data-hg15-video]');
    const control = panel.querySelector('[data-hg15-control]');
    control.hidden = !video.dataset.src;
    video.addEventListener('playing', () => {
      if (document.hidden || panel.dataset.playRequested !== 'true') { pause(panel); return; }
      frame.classList.add('is-ready');
      updateAccessibility();
      control.textContent = 'Pause video';
      control.setAttribute('aria-label', 'Pause video');
    });
    video.addEventListener('pause', () => {
      control.textContent = 'Play video';
      control.setAttribute('aria-label', 'Play video');
    });
    video.addEventListener('loadeddata', () => {
      if (panel.dataset.playRequested !== 'true') return;
      frame.classList.add('is-ready');
      control.hidden = false;
      updateAccessibility();
    });
    video.addEventListener('error', () => {
      video.dataset.failed = 'true';
      frame.classList.remove('is-ready');
      control.disabled = true;
      control.textContent = 'Video unavailable';
      control.setAttribute('aria-label', 'Video unavailable');
      if (active === panel) active = null;
      updateAccessibility();
    });
    control.addEventListener('click', () => {
      if (panel.dataset.playRequested !== 'true') {
        panel.dataset.userPaused = 'false';
        active = panel;
        play(panel, true);
        if (video.readyState >= 2) frame.classList.add('is-ready');
        updateAccessibility();
      } else {
        panel.dataset.userPaused = 'true';
        pause(panel);
      }
    });
  }

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) ratios.set(entry.target, entry.intersectionRatio);
      choose();
    }, { threshold: [0, .15, .25, .45, .65, .85, 1] });
    panels.forEach((panel) => observer.observe(panel));
  }

  document.addEventListener('visibilitychange', choose);
  const onMotionPreferenceChange = () => {
    if (!automaticMotionAllowed() && active) pause(active);
    choose();
  };
  if (typeof reducedMotion.addEventListener === 'function') reducedMotion.addEventListener('change', onMotionPreferenceChange);
  else if (typeof reducedMotion.addListener === 'function') reducedMotion.addListener(onMotionPreferenceChange);
  new MutationObserver(onMotionPreferenceChange).observe(document.documentElement, { attributes: true, attributeFilter: ['data-motion'] });
})();
